import React, { useEffect, useContext, useState } from "react";
import { useForm, ValidationError } from "@formspree/react";
import resume from "../documents/GURUPRAKASH K RESUME.pdf";
import { motion } from "framer-motion";
import { CursorContext } from "../context/CursorContext";
import { toast } from "react-toastify";
import { BsArrowRight } from "react-icons/bs";
import { transition1 } from "../transitions";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.65, ease: "easeOut" },
  }),
};

const Contact = () => {
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);
  const [state, handleSubmit] = useForm("mwpbaqan");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const notify = (type, msg) => {
    if (type === "success") toast.success(msg, { theme: "dark" });
    else toast.error(msg, { theme: "dark" });
  };

  useEffect(() => {
    if (state.succeeded) {
      notify("success", "Message sent successfully ✓");
      setName("");
      setEmail("");
      setMessage("");
    }
  }, [state.succeeded]);

  useEffect(() => {
    if (state.errors && state.errors.length > 0) {
      notify("error", "Something went wrong. Try again.");
    }
  }, [state.errors]);

  return (
    <motion.section
      id="contact"
      className="section snap-start min-h-screen flex flex-col lg:pt-36 pt-24 overflow-y-auto bg-beige dark:bg-mate"
      initial={{ opacity: 0, y: "5%" }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, amount: 0.15 }}
      exit={{ opacity: 0, y: "5%" }}
      transition={transition1}
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
    >
      <div className="container mx-auto px-6 flex flex-col h-full max-w-5xl">
        {/* ── Header ── */}
        <motion.div
          className="mb-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
        >
          <motion.h1 className="h1 font-secondary" variants={fadeUp} custom={0}>
            Contact me
          </motion.h1>
          <motion.p
            className="mt-2 text-sm leading-relaxed max-w-4xl"
            variants={fadeUp}
            custom={1}
          >
            Have a project in mind, a question, or just want to say hi? I'd love
            to hear from you.
          </motion.p>
        </motion.div>

        {/* ── Layout: Form + Info side by side on lg ── */}
        <div className="flex flex-col lg:flex-row gap-10 flex-1">
          {/* ── Contact Form ── */}
          <motion.form
            className="flex flex-col gap-5 lg:w-2/3"
            autoComplete="off"
            onSubmit={handleSubmit}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
          >
            {/* Name + Email row */}
            <motion.div
              className="flex flex-col sm:flex-row gap-5"
              variants={fadeUp}
              custom={2}
            >
              <div className="flex-1 flex flex-col gap-1">
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                  Name
                </label>
                <input
                  className="outline-none border-b border-slate-300 dark:border-slate-600 h-[48px] bg-transparent text-sm w-full placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:border-slate-500 dark:focus:border-slate-400 transition-colors"
                  type="text"
                  placeholder="Your name"
                  required
                  name="text_name"
                  autoComplete="off"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                  Email
                </label>
                <input
                  className="outline-none border-b border-slate-300 dark:border-slate-600 h-[48px] bg-transparent text-sm w-full placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:border-slate-500 dark:focus:border-slate-400 transition-colors"
                  type="email"
                  placeholder="your@email.com"
                  required
                  autoComplete="off"
                  name="text_email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <ValidationError
                  prefix="Email"
                  field="email"
                  className="text-red-400 text-xs"
                  errors={state.errors}
                />
              </div>
            </motion.div>

            {/* Message */}
            <motion.div
              className="flex flex-col gap-1"
              variants={fadeUp}
              custom={3}
            >
              <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                Message
              </label>
              <textarea
                className="outline-none border-b border-slate-300 dark:border-slate-600 bg-transparent text-sm w-full placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:border-slate-500 dark:focus:border-slate-400 transition-colors resize-none pt-2"
                rows={4}
                placeholder="Tell me about your project or idea..."
                required
                name="text_message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </motion.div>

            {/* Buttons */}
            <motion.div
              className="flex flex-row gap-4 pt-2"
              variants={fadeUp}
              custom={4}
            >
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className="btn self-start"
                type="submit"
                disabled={state.submitting}
              >
                {state.submitting ? "Sending..." : "Send it"}
                <span className="ml-1">
                  <BsArrowRight />
                </span>
              </motion.button>

              <motion.a
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                href={resume}
                download="GURUPRAKASH_K_RESUME.pdf"
                className="btn self-start"
              >
                Download CV
                <span className="ml-1">
                  <BsArrowRight />
                </span>
              </motion.a>
            </motion.div>
          </motion.form>

          {/* ── Info sidebar ── */}
          <motion.div
            className="lg:w-1/3 flex flex-col gap-5 pt-2"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
          >
            {[
              {
                label: "Email",
                value: "itsguruleo@gmail.com",
                href: "mailto:itsguruleo@gmail.com",
              },
              {
                label: "LinkedIn",
                value: "guruprakash-leo",
                href: "https://www.linkedin.com/in/guruprakash-leo",
              },
              {
                label: "GitHub",
                value: "Guru-Leo-Dev",
                href: "https://github.com/Guru-Leo-Dev",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                custom={i + 2}
                className="border-b border-slate-200 dark:border-slate-700 pb-4"
              >
                <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-1">
                  {item.label}
                </div>
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm hover:text-teal-500 transition-colors"
                >
                  {item.value}
                </a>
              </motion.div>
            ))}

            <motion.div variants={fadeUp} custom={6} className="pt-2">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-2">
                Based in
              </div>
              <p className="text-sm">Coimbatore, India 🇮🇳</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* ── Footer pinned to bottom of section ── */}
      <div className="mt-auto w-full text-center p-4 text-sm backdrop-blur-md bg-beige/30 dark:bg-black/30">
        Copyright ©️{new Date().getFullYear()} All rights reserved | This
        website is made by GuruLeo🍃
      </div>
    </motion.section>
  );
};

export default Contact;
